import type { DeprecatedToggleOption } from "@v1/components/designSystem/toggle/ToggleGroup";
import type { ReploSpecificComponentIssue } from "@v1/types/component-issues";
import type {
  AlchemyAnimationTrigger,
  AlchemyAnimationTriggerType,
  AlchemyAnimationType,
  Animation,
} from "schemas/animations";
import type { MediaSize } from "schemas/breakpoints";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import SelectableChip from "@v1/components/designSystem/chip/SelectableChip";
import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import RightBarIssues from "@v1/components/editor/page/element-editor/components/extras/RightBarIssues";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectDraftComponentAnimationIssues,
  selectDraftComponentAnimations,
  selectDraftComponentNodeFromActiveCanvas,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { getAnimationField, getAnimationName } from "@v1/utils/animations";
import { hasAnimationIssues } from "@v1/utils/getIssuesForComponent";
import { styleAttributeToEditorData } from "@v1/utils/styleAttribute";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useAreModalsOpen } from "@v2/store/modalStore";
import startCase from "lodash-es/startCase";
import { Clock, Plus, Zap } from "lucide-react";
import { getDeviceNameFromMediaSize } from "replo-runtime/shared/utils/breakpoints";
import {
  animationTriggerTypes,
  disableAnimation,
  getAnimationTriggerType,
  getAnimationValueFromType,
  resetAnimation,
} from "replo-runtime/store/utils/animations";
import { isNotNullish } from "replo-utils/lib/misc";
import { AlchemyAnimationTriggers } from "schemas/animations";
import { v4 as uuidv4 } from "uuid";

import DocumentationInfoIcon from "../DocumentationInfoIcon";
import ModifierLabel from "../extras/ModifierLabel";

const AnimationsModifier: React.FC = () => {
  const animations = useEditorSelector(selectDraftComponentAnimations);
  const animationIssues = useEditorSelector(
    selectDraftComponentAnimationIssues,
  );
  const areModalsOpen = useAreModalsOpen();
  const applyComponentAction = useApplyComponentAction();
  const isDisabled = AlchemyAnimationTriggers.some((trigger) => {
    return animations?.some((a) => a.trigger?.type === trigger);
  });
  let shouldOpenAnimationWithError = false;
  const logEvent = useLogAnalytics();

  const createAnimation = () => {
    const newAnimation = {
      id: uuidv4(),
      type: "fadeIn" as AlchemyAnimationType,
      value: getAnimationValueFromType("fadeIn"),
      trigger: {
        type: "onViewportEnter",
        value: null,
      } as AlchemyAnimationTrigger,
      // Note (Sebas, 2024-04-24): We set devices as an empty array because
      // it will be calculated inside the addAnimation action. It will apply
      // the ones that are not already set.
      devices: [],
      runOnlyOnce: true,
    };

    applyComponentAction({
      type: "addAnimation",
      value: newAnimation,
    });

    logEvent("editor.rightbar.interaction.add", {
      type: "animation",
      subType: newAnimation.type,
    });
  };

  const updateAnimation = (
    value: { id: Animation["id"] } & Partial<Animation>,
    resetValues?: boolean,
  ) => {
    applyComponentAction({
      type: "updateAnimation",
      value,
      resetValues,
    });
  };

  return (
    <ModifierGroup
      title="Animations"
      icon={Plus}
      titleEnhancer={
        <DocumentationInfoIcon href="https://support.replo.app/articles/1716423267-animations" />
      }
      iconTooltip={
        isDisabled
          ? "You can only add one animation per trigger"
          : "Add Animation"
      }
      isDisabled={isDisabled}
      onClick={createAnimation}
      hideEndEnhancerOnGroupClosed={false}
      iconShouldOpenModifierGroup
    >
      {isNotNullish(animations) && animations.length > 0 ? (
        <div className="w-full">
          {animations.map((animation) => {
            const hasIssues = hasAnimationIssues(animation.id, animationIssues);
            if (hasIssues && !shouldOpenAnimationWithError) {
              shouldOpenAnimationWithError = true;
            }
            return (
              <Popover
                key={animation.id}
                shouldPreventDefaultOnInteractOutside={areModalsOpen}
                skipTriggerStyles
                triggerAsChild
                title="Configure Animation"
                isDefaultOpen={shouldOpenAnimationWithError}
                content={
                  <AnimationEditor
                    animation={animation}
                    onUpdate={updateAnimation}
                  />
                }
              >
                <div className={twMerge("w-full", hasIssues && "opacity-50")}>
                  <SelectionIndicator
                    className="mb-2 cursor-pointer"
                    startEnhancer={
                      <Badge type="icon" icon={<Zap size={14} />} isFilled />
                    }
                    title={getAnimationName(
                      animation.trigger?.type as AlchemyAnimationTriggerType,
                      animation.type,
                    )}
                    endEnhancer={
                      <FormFieldXButton
                        onClick={(e) => {
                          e.stopPropagation();
                          applyComponentAction({
                            type: "deleteAnimation",
                            value: animation.id,
                          });
                        }}
                      />
                    }
                  />
                </div>
              </Popover>
            );
          })}
          {animationIssues.length > 0 ? (
            <RightBarIssues issues={animationIssues} />
          ) : null}
        </div>
      ) : (
        <div
          className="text-muted mx-auto w-full cursor-pointer"
          onClick={createAnimation}
        >
          <div className="text-left text-xs">
            <span>Click the + icon to add an animation</span>
          </div>
        </div>
      )}
    </ModifierGroup>
  );
};

const AnimationEditor: React.FC<{
  animation: Animation | null;
  onUpdate: (
    value: { id: Animation["id"] } & Partial<Animation>,
    resetValues?: boolean,
  ) => void;
}> = ({ animation, onUpdate }) => {
  const animations = useEditorSelector(selectDraftComponentAnimations);
  const draftComponentNode = useEditorSelector(
    selectDraftComponentNodeFromActiveCanvas,
  );
  const animationIssues = useEditorSelector(
    selectDraftComponentAnimationIssues,
  );

  if (!animation) {
    return null;
  }
  const animationWithRepeatedDevicesIssue =
    animationIssues.find(
      (
        issue,
      ): issue is ReploSpecificComponentIssue<"animations.animationWithRepeatedDevices"> => {
        return issue.type === "animations.animationWithRepeatedDevices";
      },
    )?.animationWithIssuesIdentifiers ?? null;

  const options = Object.keys(animationTriggerTypes).map((triggerType) => ({
    label: triggerType,
    value: triggerType,
  }));

  const triggerOptions = AlchemyAnimationTriggers.map((trigger) => ({
    label: startCase(trigger),
    value: trigger,
    isDisabled: animations?.some((a) => a.trigger?.type === trigger),
  }));

  const animationDurationOptions = ["Reset", "300ms", "1000ms"].map((v) => ({
    label: v,
    value: v === "Reset" ? "" : v,
  }));

  const duration = styleAttributeToEditorData.animationDuration.defaultValue;
  const delay = styleAttributeToEditorData.animationDelay.defaultValue;

  const triggerType = getAnimationTriggerType(animation.type);
  const triggers = triggerType ? animationTriggerTypes[triggerType] : null;

  let animationTypeOptions: DeprecatedToggleOption[] = [];
  if (triggers?.variants) {
    animationTypeOptions = triggers.variants.map((animationType) => ({
      label: startCase(animationType),
      value: animationType,
    }));
  }

  const onAnimationDeviceChange = (device: "sm" | "md" | "lg") => {
    const animationDevices = animation?.devices || [];
    const newDevices = animationDevices.includes(device)
      ? animationDevices.filter((d) => {
          return device === "lg" ? !["lg"].includes(d) : d !== device;
        })
      : animationDevices.concat(device === "lg" ? ["lg"] : device);
    onUpdate({
      id: animation.id,
      devices: newDevices,
    });
  };

  return (
    <div className="flex flex-col justify-between gap-y-2 py-1">
      <div className="flex flex-col gap-y-3">
        <div className="flex w-full items-center">
          <ModifierLabel label="Trigger" />
          <Selectable
            value={animation?.trigger?.type}
            options={triggerOptions}
            layoutClassName="w-full"
            triggerLayoutClassName="w-full"
            placeholder="Choose Animation Trigger"
            onSelect={(triggerType: AlchemyAnimationTriggerType) => {
              onUpdate({
                id: animation.id,
                trigger: {
                  type: triggerType,
                  value: null,
                },
              });
            }}
          />
        </div>
        <div className="flex w-full items-center">
          <ModifierLabel label="Animation" />
          <Selectable
            value={triggerType}
            options={options}
            layoutClassName="w-full"
            triggerLayoutClassName="w-full"
            placeholder="Choose Animation Type"
            popoverLayoutClassName="w-24"
            onSelect={(triggerType) => {
              if (triggerType) {
                const newTriggers = animationTriggerTypes[triggerType];
                const type = newTriggers?.variants[0];
                if (type) {
                  onUpdate(
                    {
                      id: animation.id,
                      value: getAnimationValueFromType(type),
                      type,
                    },
                    // NOTE (Sebas, 2024-04-24): We want to reset the values when the trigger type changes
                    // to avoid having invalid values for the new trigger type.
                    true,
                  );
                }
              }
            }}
          />
        </div>
        <div className="border-border border-b"></div>
        <DeprecatedToggleGroup
          size="sm"
          options={animationTypeOptions}
          selectedValue={animation.type}
          onChange={(type) => {
            const animationType = type as AlchemyAnimationType;
            onUpdate({
              id: animation.id,
              value: getAnimationValueFromType(animationType),
              type: animationType,
            });
          }}
        />
        {animationTriggerTypes[triggerType!]?.fields?.map((field) => {
          const onChange = (value: string) => {
            const option = field.options.find((o) => o.value === value);
            if (!option) {
              return;
            }
            onUpdate({
              id: animation.id,
              value: {
                styles: {
                  ...option.override,
                },
                [field.id]: option.value,
              },
            });
          };

          return (
            <div key={field.name} className="flex items-center">
              <ModifierLabel label={field.name} />
              <div key={animation.id} className="flex w-full flex-col">
                {getAnimationField({
                  animation,
                  field,
                  onChange,
                })}
              </div>
            </div>
          );
        })}
        <div className="flex flex-col">
          <div className="flex w-full items-center justify-between">
            <ModifierLabel label="Duration" />
            <LengthInputSelector
              metrics={["ms"]}
              startEnhancer={<Clock size={12} />}
              field="animationDuration"
              resetValue={duration}
              anchorValue={duration}
              placeholder={duration}
              draggingType="vertical"
              value={animation?.value?.styles?.animationDuration ?? duration}
              onChange={(value: string) => {
                onUpdate({
                  id: animation.id,
                  value: {
                    styles: {
                      animationDuration: value,
                    },
                  },
                });
              }}
              menuOptions={animationDurationOptions}
              autofocus
            />
          </div>
        </div>
        <div className="flex w-full items-center justify-between">
          <ModifierLabel label="Delay" />
          <LengthInputSelector
            metrics={["ms"]}
            startEnhancer={<Clock size={12} />}
            field="animationDelay"
            resetValue={delay}
            anchorValue={duration}
            placeholder={delay}
            draggingType="vertical"
            value={animation?.value?.styles?.animationDelay ?? delay}
            onChange={(value: string) => {
              onUpdate({
                id: animation.id,
                value: {
                  styles: {
                    animationDelay: value,
                  },
                },
              });
            }}
            menuOptions={animationDurationOptions}
          />
        </div>
        <SwitchWithDescription
          isOn={animation.runOnlyOnce !== false}
          onChange={(checked) => {
            onUpdate({ id: animation.id, runOnlyOnce: checked });
          }}
          label="Run only once"
          size="sm"
        />
        <div className="flex flex-col">
          <div className="text-muted mb-1 text-xs">Devices</div>
          <div className="flex gap-1">
            {(["sm", "md", "lg"] as MediaSize[]).map((device: MediaSize) => {
              return (
                <SelectableChip
                  key={device}
                  size="sm"
                  layoutClassName="w-1/3"
                  active={animation?.devices?.includes(device)}
                  disabled={animationWithRepeatedDevicesIssue?.[
                    animation.id
                  ]?.includes(device)}
                  onChange={() => onAnimationDeviceChange(device)}
                >
                  {getDeviceNameFromMediaSize(device)}
                </SelectableChip>
              );
            })}
          </div>
        </div>
        <DeprecatedButton
          layoutClassName="w-full"
          variant="primary"
          disabled={!animations?.some((a) => a.id === animation.id)}
          onClick={() => {
            if (draftComponentNode) {
              resetAnimation(
                draftComponentNode,
                animation.value?.styles?.animationName,
                false,
              );
              draftComponentNode.style.animationPlayState = "running";
              draftComponentNode.addEventListener(
                "animationend",
                () => {
                  disableAnimation(draftComponentNode);
                },
                {
                  once: true,
                },
              );
            }
          }}
        >
          Preview
        </DeprecatedButton>
      </div>
    </div>
  );
};

export default AnimationsModifier;
